/*
 * The BetterFX page's setup section (sections/betterfx-setup.liquid).
 * Three steps play as one story on a small desktop: the order page's
 * download button for the visitor's own system downloads the installer;
 * the installer (macOS Installer on a Mac, Windows setup elsewhere) puts
 * the BetterFX app and the Premiere Pro plugin in place; in Premiere
 * a clip is selected, ⌥Space (Alt+Space) opens BetterFX at the pointer,
 * "gaus" finds Gaussian Blur and Return applies it. The step cards light
 * up in time and jump to their step when clicked.
 *
 * Plays only while on screen and the tab is visible; leaving stops it and
 * it starts over next time. With reduced motion (or without script) each
 * step shows as a finished still, and the cards switch between them.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STOP = {};
  const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent;
  const isMac = /mac|iphone|ipad|ipod/i.test(platform);
  const WIN = { '⌘': 'Ctrl', '⌃': 'Ctrl', '⌥': 'Alt', '⇧': 'Shift' };
  // Running text: "press ⌥Space" becomes "press Alt+Space" off a Mac.
  const forOs = (text) =>
    isMac ? text : text.replace(/([⌃⌥⇧⌘]+)(\S)/g, (m, mods, key) => [...mods].map((c) => WIN[c]).concat(key).join('+'));
  const OPEN_KEYS = isMac ? ['⌥', 'Space'] : ['Alt', 'Space'];
  const APPLY_KEYS = [isMac ? 'Return' : 'Enter'];
  // How long each step plays, in ms (the waits in its script add up to this).
  const LENGTH = { 1: 5450, 2: 5700, 3: 8200 };

  const init = (root) => {
    if (root.dataset.suReady) return;
    root.dataset.suReady = 'true';

    const q = (sel) => root.querySelector(sel);
    const stage = q('[data-su-stage]');
    if (!stage) return;
    const web = q('[data-su-web]');
    const primary = q('[data-su-primary]');
    const primaryLabel = q('[data-su-primary-label]');
    const secondary = q('[data-su-secondary]');
    const file = q('[data-su-file]');
    const pr = q('[data-su-pr]');
    const clipEl = q('[data-su-clip]');
    const screen = q('[data-su-screen]');
    const pic = q('[data-su-pic]');
    const bfx = q('[data-su-bfx]');
    const query = q('[data-su-q]');
    const keys = q('[data-su-keys]');
    const cursor = q('[data-su-cursor]');
    const cards = [...root.querySelectorAll('[data-su-card]')];
    const buttons = [...root.querySelectorAll('[data-su-go]')];
    const fills = cards.map((card) => card.querySelector('.su-step-fill'));
    const scenes = [...stage.querySelectorAll('.su-scene')];
    if (!web || !pr || !bfx || !cursor || scenes.length !== 3) return;

    // This visitor's system: its installer, its window chrome, its keys.
    stage.classList.toggle('is-mac', isMac);
    stage.classList.toggle('is-win', !isMac);
    const inst = stage.querySelector(isMac ? '.su-inst.su-only-mac' : '.su-inst.su-only-win');
    const installBtn = inst && inst.querySelector('[data-su-go-btn]');
    const macSteps = [...stage.querySelectorAll('[data-su-m]')];
    const d = root.dataset;
    const ownLabel = isMac ? d.macLabel : d.winLabel;
    if (secondary) secondary.textContent = isMac ? d.winLabel : d.macLabel;
    const ext = q('[data-su-ext]');
    if (ext) ext.textContent = isMac ? 'PKG' : 'EXE';
    root.querySelectorAll('[data-su-os]').forEach((el) => (el.textContent = forOs(el.textContent)));
    const typed = d.search || 'gaus';

    // The clip's blurred look, for when the effect goes on.
    if (pic && !screen.querySelector('.su-pic--blur')) {
      const blur = pic.cloneNode(true);
      blur.classList.add('su-pic--blur');
      blur.removeAttribute('data-su-pic');
      screen.appendChild(blur);
    }

    const showKeys = (list) => {
      keys.innerHTML = list.map((k) => `<kbd>${k}</kbd>`).join('');
    };

    const setStep = (n) => {
      stage.dataset.step = String(n);
      cards.forEach((card, i) => {
        const on = i + 1 === n;
        card.classList.toggle('is-current', on);
        if (on) buttons[i].setAttribute('aria-current', 'step');
        else buttons[i].removeAttribute('aria-current');
      });
    };

    const fillCard = (n, ms) => {
      fills.forEach((fill, i) => {
        if (!fill) return;
        fill.style.transition = 'none';
        fill.style.transform = 'scaleX(0)';
        if (i + 1 === n && ms) {
          void fill.offsetWidth;
          fill.style.transition = `transform ${ms}ms linear`;
          fill.style.transform = 'scaleX(1)';
        }
      });
    };

    const setMac = (state) => macSteps.forEach((li) => li.classList.toggle('is-now', li.dataset.suM === state));

    // Each scene's details, at their start (snapped, no animation).
    const snap = (el, fn) => {
      el.classList.add('is-instant');
      fn();
      void el.offsetWidth;
      el.classList.remove('is-instant');
    };
    const resetWeb = () =>
      snap(web, () => {
        web.classList.remove('is-down', 'is-got', 'is-open');
        primaryLabel.textContent = ownLabel;
      });
    const resetInst = () =>
      snap(inst, () => {
        inst.classList.remove('is-run', 'is-app', 'is-int', 'is-done');
        setMac('ready');
      });
    const resetPr = () =>
      snap(pr, () => {
        pr.classList.remove('is-sel', 'is-applied');
        bfx.classList.remove('is-open', 'has-q');
        query.textContent = '';
      });

    // The finished still of each step.
    const doneWeb = () =>
      snap(web, () => {
        web.classList.remove('is-down', 'is-open');
        web.classList.add('is-got');
        primaryLabel.textContent = d.downloaded;
      });
    const doneInst = () =>
      snap(inst, () => {
        inst.classList.add('is-run', 'is-app', 'is-int', 'is-done');
        setMac('done');
      });
    const donePr = () =>
      snap(pr, () => {
        pr.classList.remove('is-applied');
        pr.classList.add('is-sel');
        query.textContent = typed;
        bfx.classList.add('is-open', 'has-q');
        bfx.style.setProperty('--bx', '50%');
        bfx.style.setProperty('--by', '47%');
      });

    let run = 0;
    let playing = false;
    let inView = false;

    const wait = (r, ms) =>
      new Promise((resolve, reject) => {
        window.setTimeout(() => (r === run ? resolve() : reject(STOP)), ms);
      });

    // Where an element sits inside the stage, in px.
    const spot = (el, fx = 0.5, fy = 0.5) => {
      const s = stage.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      return [b.left - s.left + b.width * fx, b.top - s.top + b.height * fy];
    };
    const cursorAt = (x, y) => {
      cursor.classList.add('is-jump');
      cursor.style.setProperty('--cx', `${x}px`);
      cursor.style.setProperty('--cy', `${y}px`);
      void cursor.offsetWidth;
      cursor.classList.remove('is-jump');
    };
    const cursorIn = () => {
      const s = stage.getBoundingClientRect();
      cursorAt(s.width * 0.82, s.height * 0.92);
      cursor.classList.add('is-on');
    };
    const moveTo = (r, el, fx, fy, ms = 650) => {
      const [x, y] = spot(el, fx, fy);
      cursor.style.setProperty('--cm', `${ms}ms`);
      cursor.style.setProperty('--cx', `${x}px`);
      cursor.style.setProperty('--cy', `${y}px`);
      return wait(r, ms);
    };
    const tap = (r) => {
      cursor.classList.remove('is-click');
      void cursor.offsetWidth;
      cursor.classList.add('is-click');
      return wait(r, 200);
    };
    const press = async (r, list) => {
      showKeys(list);
      keys.classList.add('is-on');
      await wait(r, 160);
      keys.querySelectorAll('kbd').forEach((k) => k.classList.add('is-down'));
      await wait(r, 260);
      keys.querySelectorAll('kbd').forEach((k) => k.classList.remove('is-down'));
      await wait(r, 240);
    };

    // ---- 1. Download ----
    const step1 = async (r) => {
      resetWeb();
      keys.classList.remove('is-on');
      setStep(1);
      fillCard(1, LENGTH[1]);
      await wait(r, 700);
      cursorIn();
      await moveTo(r, primary, 0.5, 0.62, 800);
      await tap(r);
      primaryLabel.textContent = d.downloading;
      web.classList.add('is-down');
      await wait(r, 1700);
      web.classList.remove('is-down');
      web.classList.add('is-got');
      primaryLabel.textContent = d.downloaded;
      await wait(r, 700);
      await moveTo(r, file, 0.45, 0.55, 700);
      await tap(r);
      web.classList.add('is-open');
      await wait(r, 450);
    };

    // ---- 2. Install ----
    const step2 = async (r) => {
      resetInst();
      setStep(2);
      fillCard(2, LENGTH[2]);
      // the pointer carries on from the download it just opened
      await wait(r, 700);
      if (!cursor.classList.contains('is-on')) cursorIn();
      await moveTo(r, installBtn, 0.5, 0.6, 700);
      await tap(r);
      inst.classList.add('is-run');
      setMac('run');
      await wait(r, 950);
      inst.classList.add('is-app');
      await wait(r, 1150);
      inst.classList.add('is-int');
      await wait(r, 450);
      inst.classList.add('is-done');
      setMac('done');
      cursor.classList.remove('is-on');
      await wait(r, 1550);
    };

    // ---- 3. Press the shortcut ----
    const step3 = async (r) => {
      resetPr();
      keys.classList.remove('is-on');
      setStep(3);
      fillCard(3, LENGTH[3]);
      await wait(r, 700);
      cursorIn();
      await moveTo(r, clipEl, 0.55, 0.5, 750);
      await tap(r);
      pr.classList.add('is-sel');
      await wait(r, 350);
      await moveTo(r, screen, 0.5, 0.66, 600);
      await press(r, OPEN_KEYS);
      // BetterFX opens centred on the pointer, kept inside the window
      const w = pr.getBoundingClientRect();
      const c = cursor.getBoundingClientRect();
      const barW = Math.min(360, w.width - 20);
      const bx = Math.max(barW / 2 + 10, Math.min(w.width - barW / 2 - 10, c.left - w.left));
      const by = Math.max(37, Math.min(w.height - 104, c.top - w.top));
      bfx.style.setProperty('--bx', `${bx}px`);
      bfx.style.setProperty('--by', `${by}px`);
      bfx.classList.add('is-open');
      cursor.classList.remove('is-on');
      await wait(r, 450);
      for (const ch of typed) {
        query.textContent += ch;
        bfx.classList.add('has-q');
        await wait(r, 125);
      }
      await wait(r, 750);
      await press(r, APPLY_KEYS);
      bfx.classList.remove('is-open');
      pr.classList.add('is-applied');
      await wait(r, 900);
      keys.classList.remove('is-on');
      await wait(r, 1690);
    };

    const STEPS = [step1, step2, step3];

    const play = async (from = 1) => {
      run += 1;
      const r = run;
      playing = true;
      let n = from;
      try {
        for (;;) {
          await STEPS[n - 1](r);
          n = n === 3 ? 1 : n + 1;
        }
      } catch (error) {
        if (error !== STOP) throw error;
      }
    };

    // Back to the very start, ready for the next time it is seen.
    const stop = () => {
      run += 1;
      playing = false;
      cursor.classList.remove('is-on');
      keys.classList.remove('is-on');
      resetWeb();
      resetInst();
      resetPr();
      fillCard(0);
      setStep(1);
    };

    // Stills: every step finished, the chosen one showing.
    const showStill = (n) => {
      doneWeb();
      doneInst();
      donePr();
      showKeys(OPEN_KEYS);
      keys.classList.toggle('is-on', n === 3);
      fillCard(0);
      setStep(n);
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const n = Number(button.dataset.suGo) || 1;
        if (!playing) {
          showStill(n);
          return;
        }
        cursor.classList.remove('is-on');
        keys.classList.remove('is-on');
        play(n);
      });
    });

    showKeys(OPEN_KEYS);
    if (still.matches || !('IntersectionObserver' in window)) {
      showStill(3);
      return;
    }

    stop();

    const update = () => {
      const go = inView && !document.hidden;
      if (go && !playing) play(1);
      else if (!go && playing) stop();
    };

    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;
        });
        update();
      },
      { threshold: 0.35 }
    ).observe(stage);
    document.addEventListener('visibilitychange', update);
  };

  const boot = () => document.querySelectorAll('[data-betterfx-setup]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
